import Phaser from "phaser";
import { listSaves, type MinimalSave } from "../save/saveStore";

export type SaveLoadMode = "save" | "load";

export interface SaveLoadResult {
  cancel: boolean;
  slot?: string;
}

/**
 * セーブ/ロード画面(モーダル)。
 * 6 スロット + auto + quick を表示する。
 */
export class SaveLoadMenu {
  private container: Phaser.GameObjects.Container;
  private resolver: ((res: SaveLoadResult) => void) | null = null;
  private buttons: Phaser.GameObjects.Rectangle[] = [];

  constructor(private scene: Phaser.Scene, private gameId: string) {
    this.container = scene.add.container(0, 0).setVisible(false).setDepth(100);
  }

  open(mode: SaveLoadMode): Promise<SaveLoadResult> {
    this.cleanup();

    const scene = this.scene;
    const w = scene.scale.width;
    const h = scene.scale.height;

    // 暗幕
    const dim = scene.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.7);
    this.container.add(dim);

    // タイトル
    const title = scene.add
      .text(w / 2, 80, mode === "save" ? "SAVE" : "LOAD", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "32px",
        color: "#ffffff",
      })
      .setOrigin(0.5);
    this.container.add(title);

    // 既存セーブ一覧
    const saves = new Map(listSaves(this.gameId).map((s) => [s.slot, s.data]));

    const slots: string[] = ["auto", "quick", "1", "2", "3", "4", "5", "6"];
    const cols = 2;
    const rows = Math.ceil(slots.length / cols);
    const cellW = 480;
    const cellH = 90;
    const gridW = cols * cellW + (cols - 1) * 16;
    const gridH = rows * cellH + (rows - 1) * 14;
    const startX = (w - gridW) / 2;
    const startY = 140;

    slots.forEach((slot, i) => {
      const cx = startX + (i % cols) * (cellW + 16) + cellW / 2;
      const cy = startY + Math.floor(i / cols) * (cellH + 14) + cellH / 2;
      const existing = saves.get(slot);
      const isEmpty = !existing;
      const bg = scene.add
        .rectangle(cx, cy, cellW, cellH, isEmpty ? 0x222230 : 0x1d3050, 0.95)
        .setStrokeStyle(2, isEmpty ? 0x444466 : 0x66aaff)
        .setInteractive({ useHandCursor: true });

      const headLabel = scene.add
        .text(cx - cellW / 2 + 16, cy - cellH / 2 + 10, slotLabel(slot), {
          fontFamily: "system-ui, sans-serif",
          fontSize: "14px",
          color: "#cce6ff",
        })
        .setOrigin(0, 0);

      const dateText = existing
        ? formatDate(existing.timestamp)
        : "(empty)";
      const bodyText = existing
        ? snippetFromSave(existing)
        : mode === "save"
          ? "クリックでセーブ"
          : "—";

      const meta = scene.add
        .text(cx - cellW / 2 + 16, cy - cellH / 2 + 32, dateText, {
          fontFamily: "monospace",
          fontSize: "12px",
          color: "#7799bb",
        })
        .setOrigin(0, 0);

      const body = scene.add
        .text(cx - cellW / 2 + 16, cy - cellH / 2 + 56, bodyText, {
          fontFamily: "system-ui, sans-serif",
          fontSize: "14px",
          color: "#ffffff",
          wordWrap: { width: cellW - 32 },
        })
        .setOrigin(0, 0);

      bg.on("pointerover", () =>
        bg.setFillStyle(isEmpty ? 0x33334a : 0x2a4570, 1.0),
      );
      bg.on("pointerout", () =>
        bg.setFillStyle(isEmpty ? 0x222230 : 0x1d3050, 0.95),
      );
      bg.on("pointerdown", () => {
        if (mode === "load" && isEmpty) return;
        this.resolveResult({ cancel: false, slot });
      });

      this.buttons.push(bg);
      this.container.add([bg, headLabel, meta, body]);
    });

    // キャンセル
    const cancelBg = scene.add
      .rectangle(w / 2, h - 60, 200, 44, 0x442222, 0.95)
      .setStrokeStyle(2, 0xff8888)
      .setInteractive({ useHandCursor: true });
    const cancelLbl = scene.add
      .text(w / 2, h - 60, "戻る", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "18px",
        color: "#ffffff",
      })
      .setOrigin(0.5);
    cancelBg.on("pointerover", () => cancelBg.setFillStyle(0x664444, 1.0));
    cancelBg.on("pointerout", () => cancelBg.setFillStyle(0x442222, 0.95));
    cancelBg.on("pointerdown", () => this.resolveResult({ cancel: true }));
    this.container.add([cancelBg, cancelLbl]);

    this.container.setVisible(true);

    return new Promise((resolve) => {
      this.resolver = resolve;
    });
  }

  private resolveResult(res: SaveLoadResult): void {
    const r = this.resolver;
    this.resolver = null;
    this.cleanup();
    this.container.setVisible(false);
    if (r) r(res);
  }

  private cleanup(): void {
    this.container.each((child: Phaser.GameObjects.GameObject) => child.destroy());
    this.container.removeAll(false);
    this.buttons = [];
  }
}

function slotLabel(slot: string): string {
  if (slot === "auto") return "AUTO";
  if (slot === "quick") return "QUICK";
  return `SLOT ${slot}`;
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function snippetFromSave(save: MinimalSave): string {
  return `${save.state.file} / cmd ${save.state.cmdIndex}`;
}
